import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fakeBackend } from '../../services/fakeBackend';
import { Exercise, Meal, HealingActivity, Challenge } from '../../types';
import { 
  FireIcon, 
  ClockIcon, 
  HeartIcon, 
  TrophyIcon,
  CalendarIcon,
  ChartBarIcon,
  PlayIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [recentExercises, setRecentExercises] = useState<Exercise[]>([]);
  const [recentMeals, setRecentMeals] = useState<Meal[]>([]);
  const [recentHealing, setRecentHealing] = useState<HealingActivity[]>([]);
  const [activeChallenges, setActiveChallenges] = useState<Challenge[]>([]);
  const [exerciseStats, setExerciseStats] = useState<{
    totalCompleted: number;
    thisWeekCompleted: number;
    totalCaloriesBurned: number;
    thisWeekCalories: number;
    categoryBreakdown: Record<string, number>;
    averageDuration: number;
  } | null>(null);
  const [healingStats, setHealingStats] = useState<{
    totalCompleted: number;
    thisWeekCompleted: number;
    totalDuration: number;
    thisWeekDuration: number;
    categoryBreakdown: Record<string, number>;
    averageDuration: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [exercisesRes, mealsRes, healingRes, challengesRes, exerciseStatsRes, completedExercisesRes, healingStatsRes, completedHealingRes] = await Promise.all([
        fakeBackend.getExercises(),
        fakeBackend.getMeals(),
        fakeBackend.getHealingActivities(),
        fakeBackend.getChallenges(),
        fakeBackend.getExerciseStats(),
        fakeBackend.getCompletedExercises(),
        fakeBackend.getHealingStats(),
        fakeBackend.getCompletedHealingActivities(),
      ]);

      if (exercisesRes.success) {
        setRecentExercises(exercisesRes.data?.slice(0, 3) || []);
      }
      if (mealsRes.success) {
        setRecentMeals(mealsRes.data?.slice(0, 3) || []);
      }
      if (healingRes.success) {
        setRecentHealing(healingRes.data?.slice(0, 3) || []);
      }
      if (challengesRes.success) {
        setActiveChallenges(challengesRes.data?.filter(c => c.isActive) || []);
      }
      if (exerciseStatsRes.success) {
        setExerciseStats(exerciseStatsRes.data || null);
      }
      if (healingStatsRes.success) {
        setHealingStats(healingStatsRes.data || null);
      }
      if (completedExercisesRes.success) {
        // Show recent completed exercises instead of all exercises
        const recentCompleted = completedExercisesRes.data
          ?.sort((a, b) => new Date(b.completedAt || '').getTime() - new Date(a.completedAt || '').getTime())
          .slice(0, 3) || [];
        setRecentExercises(recentCompleted);
      }
      if (completedHealingRes.success) {
        // Show recent completed healing activities instead of all activities
        const recentCompleted = completedHealingRes.data
          ?.sort((a, b) => new Date(b.completedAt || '').getTime() - new Date(a.completedAt || '').getTime())
          .slice(0, 3) || [];
        setRecentHealing(recentCompleted);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStreakEmoji = (streak: number) => {
    if (streak >= 7) return '🔥';
    if (streak >= 3) return '⚡';
    return '💪';
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      yoga: 'bg-purple-100 text-purple-800',
      zen: 'bg-blue-100 text-blue-800',
      calisthenics: 'bg-green-100 text-green-800',
      powerlifting: 'bg-red-100 text-red-800',
      satvik: 'bg-yellow-100 text-yellow-800',
      ayurvedic: 'bg-orange-100 text-orange-800',
      regular: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.firstName}! {getStreakEmoji(user?.exerciseStreak || 0)}
          </h1>
          <p className="mt-2 text-gray-600">
            Keep up the great work on your fitness journey
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FireIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Current Streak</p>
                <p className="text-2xl font-bold text-gray-900">{user?.exerciseStreak || 0} days</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrophyIcon className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Longest Streak</p>
                <p className="text-2xl font-bold text-gray-900">{user?.longestExerciseStreak || 0} days</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">This Week</p>
                <p className="text-2xl font-bold text-gray-900">{exerciseStats?.thisWeekCompleted || 0} exercises</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <HeartIcon className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Calories Burned</p>
                <p className="text-2xl font-bold text-gray-900">{exerciseStats?.thisWeekCalories || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Exercise Stats */}
        {exerciseStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Completed</p>
                  <p className="text-2xl font-bold text-gray-900">{exerciseStats.totalCompleted} exercises</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Avg Duration</p>
                  <p className="text-2xl font-bold text-gray-900">{exerciseStats.averageDuration} min</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-8 w-8 text-indigo-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Last Exercise</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {user?.lastExerciseDate ? new Date(user.lastExerciseDate).toLocaleDateString() : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Healing Stats */}
        {healingStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <HeartIcon className="h-8 w-8 text-pink-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Healing Streak</p>
                  <p className="text-2xl font-bold text-gray-900">{user?.healingStreak || 0} days</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Longest Healing</p>
                  <p className="text-2xl font-bold text-gray-900">{user?.longestHealingStreak || 0} days</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">This Week</p>
                  <p className="text-2xl font-bold text-gray-900">{healingStats.thisWeekCompleted} activities</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Time</p>
                  <p className="text-2xl font-bold text-gray-900">{healingStats.totalDuration} min</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/exercises"
              className="card hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <PlayIcon className="h-8 w-8 text-primary-600 group-hover:text-primary-700" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Start Exercise</h3>
                  <p className="text-sm text-gray-500">Choose from our exercise library</p>
                </div>
              </div>
            </Link>

            <Link
              to="/meals"
              className="card hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <HeartIcon className="h-8 w-8 text-green-600 group-hover:text-green-700" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Plan Meal</h3>
                  <p className="text-sm text-gray-500">Discover healthy meal options</p>
                </div>
              </div>
            </Link>

            <Link
              to="/healing"
              className="card hover:shadow-md transition-shadow cursor-pointer group"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-purple-600 group-hover:text-purple-700" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Healing Zone</h3>
                  <p className="text-sm text-gray-500">Explore wellness activities</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Exercises */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Exercises</h3>
              <Link to="/exercises" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentExercises.length > 0 ? (
                recentExercises.map((exercise) => (
                  <div key={exercise._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <img
                      src={exercise.imageUrl}
                      alt={exercise.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{exercise.name}</h4>
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(exercise.category)}`}>
                          {exercise.category}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">{exercise.duration} min</span>
                        {exercise.isCompleted && (
                          <span className="ml-2 text-xs text-green-600 font-medium">✓ Completed</span>
                        )}
                      </div>
                      {exercise.completedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Completed on {new Date(exercise.completedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{exercise.calories} cal</div>
                      <div className="text-xs text-gray-400">
                        {exercise.isCompleted ? 'View' : 'Start'}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">No exercises completed yet</p>
                  <Link to="/exercises" className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2 inline-block">
                    Start your first exercise
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Healing Activities */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Healing</h3>
              <Link to="/healing" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentHealing.length > 0 ? (
                recentHealing.map((activity) => (
                  <div key={activity._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <img
                      src={activity.imageUrl}
                      alt={activity.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="ml-3 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{activity.name}</h4>
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(activity.category)}`}>
                          {activity.category}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">{activity.duration} min</span>
                        {activity.isCompleted && (
                          <span className="ml-2 text-xs text-green-600 font-medium">✓ Completed</span>
                        )}
                      </div>
                      {activity.completedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Completed on {new Date(activity.completedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{activity.duration} min</div>
                      <div className="text-xs text-gray-400">
                        {activity.isCompleted ? 'View' : 'Start'}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">No healing activities completed yet</p>
                  <Link to="/healing" className="text-primary-600 hover:text-primary-700 text-sm font-medium mt-2 inline-block">
                    Start your first healing activity
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Meals */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Meals</h3>
              <Link to="/meals" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentMeals.map((meal) => (
                <div key={meal._id} className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <img
                    src={meal.imageUrl}
                    alt={meal.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{meal.name}</h4>
                    <div className="flex items-center mt-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(meal.category)}`}>
                        {meal.category}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">{meal.calories} cal</span>
                    </div>
                  </div>
                  <Link
                    to={`/meals/${meal._id}`}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Active Challenges */}
        {activeChallenges.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Active Challenges</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeChallenges.map((challenge) => (
                <div key={challenge._id} className="card">
                  <img
                    src={challenge.imageUrl}
                    alt={challenge.title}
                    className="w-full h-32 object-cover rounded-lg mb-4"
                  />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{challenge.title}</h3>
                  <p className="text-sm text-gray-600 mb-4">{challenge.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {challenge.participants.length} participants
                    </span>
                    <Link
                      to={`/challenges/${challenge._id}`}
                      className="btn-primary text-sm"
                    >
                      Join Challenge
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 